import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { injectIntl } from 'react-intl';
import type { topicFragment$key as TopicFragmentType } from './__generated__/topicFragment.graphql';
import { HelpObject } from '../help-object';
import { Container, TopicName, HelpObjects, ShowMoreLink, ListItem } from './styled';
import messages from './messages';
import type { InjectedIntlProps } from 'react-intl';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { AnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { isAdditionalLogsForHelpObjectNotHydratedEnabled } from 'feature-flags';

interface Props {
    topic: TopicFragmentType;
}

const Topic = (props: Props & InjectedIntlProps & WithAnalyticsEventsProps) => {
    const { name, items, topicId } = useFragment<TopicFragmentType>(
        graphql`
            fragment topicFragment on HelpCenterTopic {
                name
                topicId
                items {
                    edges {
                        ...helpObject
                        node {
                            ari
                            data {
                                __typename
                            }
                        }
                    }
                }
            }
        `,
        props.topic
    );
    const MAX_HELP_OBJECTS = 3;

    const allHelpObjects = items?.edges || [];

    if (isAdditionalLogsForHelpObjectNotHydratedEnabled()) {
        allHelpObjects.forEach((o) => {
            if (!o?.node?.data) {
                const { createAnalyticsEvent } = props;

                if (createAnalyticsEvent) {
                    const analyticsEvent = createAnalyticsEvent({
                        analyticsType: OPERATIONAL_EVENT_TYPE,
                        action: 'invalidHelpObjectFound',
                        actionSubject: 'helpObject',
                    });
                    analyticsEvent.fire();
                }
            }
        });
    }

    const visibleHelpObjects = allHelpObjects.filter((o) => o?.node?.data?.__typename !== 'HelpObjectStoreQueryError');

    const showMoreCount = visibleHelpObjects.length - MAX_HELP_OBJECTS;

    const countMessage = props.intl.formatMessage(messages.showMoreText, {
        count: showMoreCount,
    });

    if (visibleHelpObjects.length) {
        return (
            <Container>
                <TopicName to={`/topic/${topicId}`} actionSubjectId={'topicTitle'}>
                    {name}
                </TopicName>
                <HelpObjects>
                    {visibleHelpObjects.slice(0, MAX_HELP_OBJECTS).map((helpObject, index) => (
                        <ListItem key={helpObject?.node?.ari}>
                            <AnalyticsContext data={{ attributes: { helpObjectPosition: index + 1 } }}>
                                <HelpObject helpObject={helpObject} />
                            </AnalyticsContext>
                        </ListItem>
                    ))}
                </HelpObjects>
                {showMoreCount > 0 && (
                    <ShowMoreLink to={`/topic/${topicId}`} actionSubjectId={'showMoreHelpObjects'}>
                        {countMessage}
                    </ShowMoreLink>
                )}
            </Container>
        );
    }
    return null;
};

const TopicWithoutAnalyticsEvents = injectIntl(Topic);
export { TopicWithoutAnalyticsEvents };

export default withAnalyticsEvents()(injectIntl(Topic));
